import { Box, Button, HStack, Image, SimpleGrid, Stack, Text } from "@chakra-ui/react"
import { Form } from "components"
import { API, Mask, toaster } from "config"
import useUser from "hooks/useUser"
import { useEffect, useState } from "react"
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input"
import { useQueryClient } from "@tanstack/react-query"

const card = {
    background: '#161616',
    border: '1px solid #2a2a2a',
    borderRadius: '14px',
    padding: '24px',
}

const cardHeader = {
    borderBottom: '1px solid #2a2a2a',
    paddingBottom: '12px',
    marginBottom: '4px',
}

// Reconstrói o E.164 a partir de mobile_country_code + mobile separados.
// Se o campo mobile já vier com o código embutido (dado legado/corrompido),
// tenta `+mobile` diretamente antes de tentar `+countryCode+mobile`.
function buildMobileE164(countryCode, mobile) {
    if (!mobile) return ''
    const withCode = `+${countryCode ?? '55'}${mobile}`
    if (isValidPhoneNumber(withCode)) return withCode
    const direct = `+${mobile}`
    if (isValidPhoneNumber(direct)) return direct
    return withCode
}

export default function AtualizarDados({ daysRemaining, overdue, onSkip }) {

    const queryClient = useQueryClient()
    const { data: userData, isLoading: userLoading } = useUser()
    const [form, setForm] = useState({})
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (userData && !form.email) {
            setForm({
                email:        userData.email ?? '',
                mobile:       buildMobileE164(userData.mobile_country_code, userData.mobile),
                home_number:  Mask.mobile(userData.home_number ?? ''),
                work_number:  Mask.mobile(userData.work_number ?? ''),
                city_id:      userData.address?.city?.id ?? null,
                line_1:       userData.address?.line_1 ?? '',
                line_2:       userData.address?.line_2 ?? '',
                zip:          Mask.zip(userData.address?.zip ?? ''),
                neighborhood: userData.address?.neighborhood ?? '',
                number:       userData.address?.number ?? '',
                complement:   userData.address?.complement ?? '',
            })
        }
    }, [userData])

    const handleChange = ({ target }) => {
        const { name, value } = target
        const phoneFields = ['home_number', 'work_number']
        setForm(prev => ({
            ...prev,
            [name]: name === 'zip' ? Mask.zip(value)
                  : phoneFields.includes(name) ? Mask.mobile(value)
                  : value,
        }))
    }

    const handleSubmit = async () => {
        setLoading(true)
        setErrors({})

        try {
            const parsed = form.mobile ? parsePhoneNumber(form.mobile) : null
            const payload = {
                ...form,
                mobile:              parsed?.nationalNumber ?? form.mobile,
                mobile_country_code: parsed?.countryCallingCode ?? null,
            }

            await API.put('/me/mandatory-update', payload)

            toaster.create({ title: 'Dados atualizados com sucesso!', type: 'success' })
            queryClient.invalidateQueries({ queryKey: ['user'] })

        } catch (err) {
            const responseErrors = err?.response?.data?.errors ?? {}
            setErrors(responseErrors)
            toaster.create({
                title: 'Não foi possível salvar.',
                description: err?.response?.data?.message,
                type: 'warning',
            })
        }

        setLoading(false)
    }

    if (userLoading) return (
        <Box
            style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            className="chakra-theme dark"
        >
            <Stack align="center" gap={4}>
                <Image src="/icon.png" h="56px" borderRadius="lg" style={{ mixBlendMode: 'screen' }} />
                <Text color="gray.400" fontSize="sm">Carregando seus dados...</Text>
            </Stack>
        </Box>
    )

    return (
        <Box
            as="form"
            onSubmit={e => { e.preventDefault(); handleSubmit() }}
            className="chakra-theme dark"
            style={{ height: '100dvh', background: '#0a0a0a', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
            {/* ── Topbar ── */}
            <Box style={{ background: '#111111', borderBottom: '1px solid #222222', flexShrink: 0 }} px={6} py={3}>
                <HStack maxW="1100px" mx="auto" justify="space-between">
                    <HStack gap={3}>
                        <Image src="/icon.png" h="34px" borderRadius="md" style={{ mixBlendMode: 'screen' }} />
                        <Text color="white" fontWeight="bold" fontSize="md" letterSpacing="tight">
                            Bonífica
                        </Text>
                    </HStack>
                    <Text color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                        Área do Consultor
                    </Text>
                </HStack>
            </Box>

            {/* ── Scrollable content ── */}
            <Box style={{ flex: 1, overflowY: 'auto' }} px={4} py={4}>
                <Box maxW="1100px" mx="auto">

                    {/* ── Page title ── */}
                    <Stack gap={1} mb={4}>
                        <HStack gap={3} align="center">
                            <Box style={{ width: '3px', height: '28px', background: '#f97316', borderRadius: '2px', flexShrink: 0 }} />
                            <Text color="white" fontWeight="bold" fontSize="xl">
                                Confirmação de dados cadastrais
                            </Text>
                        </HStack>
                        <Text color="gray.500" fontSize="sm" pl="15px">
                            Preencha os campos abaixo para liberar o acesso completo à plataforma
                        </Text>
                    </Stack>

                    {!overdue && daysRemaining !== null && daysRemaining !== undefined && (
                        <Box
                            style={{
                                background: daysRemaining <= 1 ? '#1a0a00' : '#111800',
                                border: `1px solid ${daysRemaining <= 1 ? '#f97316' : '#84cc16'}`,
                                borderRadius: '10px',
                                padding: '10px 16px',
                                marginBottom: '16px',
                            }}
                        >
                            <HStack gap={2}>
                                <Text fontSize="sm" style={{ flexShrink: 0 }}>{daysRemaining <= 1 ? '⚠️' : '📅'}</Text>
                                <Text color={daysRemaining <= 1 ? 'orange.300' : 'green.300'} fontSize="sm">
                                    {daysRemaining === 0
                                        ? 'Último dia para atualizar seus dados antes de ser obrigatório.'
                                        : `Você tem ${daysRemaining} dia${daysRemaining !== 1 ? 's' : ''} para atualizar seus dados voluntariamente.`
                                    }
                                </Text>
                            </HStack>
                        </Box>
                    )}

                    {overdue && (
                        <Box
                            style={{
                                background: '#1a0000',
                                border: '1px solid #ef4444',
                                borderRadius: '10px',
                                padding: '10px 16px',
                                marginBottom: '16px',
                            }}
                        >
                            <HStack gap={2}>
                                <Text fontSize="sm" style={{ flexShrink: 0 }}>🔒</Text>
                                <Text color="red.300" fontSize="sm">
                                    O prazo para atualização voluntária encerrou. A confirmação dos dados é obrigatória para continuar.
                                </Text>
                            </HStack>
                        </Box>
                    )}

                    {/* ── Two columns ── */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>

                        {/* ── Contato ── */}
                        <Box style={card}>
                            <Stack gap={3}>
                                <HStack gap={2} style={cardHeader}>
                                    <Box style={{ width: '2px', height: '14px', background: '#f97316', borderRadius: '1px' }} />
                                    <Text fontSize="xs" fontWeight="bold" color="orange.400" textTransform="uppercase" letterSpacing="widest">
                                        Contato
                                    </Text>
                                </HStack>

                                <Form.Text
                                    required
                                    label="E-mail"
                                    name="email"
                                    type="email"
                                    value={form.email ?? ''}
                                    onChange={handleChange}
                                    error={errors?.email}
                                    disabled={loading}
                                    placeholder="seu@email.com"
                                />
                                <HStack gap={2} align="flex-start" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '8px 12px' }}>
                                    <Text style={{ flexShrink: 0, fontSize: '13px', marginTop: '1px' }}>⚠️</Text>
                                    <Text color="gray.400" fontSize="xs" lineHeight="tall">
                                        O e-mail é usado para login — alterá-lo mudará suas credenciais de acesso.{' '}
                                        <Text as="span" color="gray.300">Utilize um endereço válido e de acesso frequente.</Text>
                                    </Text>
                                </HStack>

                                <Form.Mobile
                                    required
                                    label="Celular / WhatsApp"
                                    name="mobile"
                                    value={form.mobile ?? ''}
                                    onChange={(val) => setForm(prev => ({ ...prev, mobile: val }))}
                                    disabled={loading}
                                    error={errors?.mobile}
                                />

                                <SimpleGrid columns={2} gap={3}>
                                    <Form.Text
                                        label="Telefone fixo"
                                        name="home_number"
                                        type="tel"
                                        value={form.home_number ?? ''}
                                        onChange={handleChange}
                                        error={errors?.home_number}
                                        disabled={loading}
                                        placeholder="Ex.: 6232001234"
                                    />
                                    <Form.Text
                                        label="Tel. comercial"
                                        name="work_number"
                                        type="tel"
                                        value={form.work_number ?? ''}
                                        onChange={handleChange}
                                        error={errors?.work_number}
                                        disabled={loading}
                                        placeholder="Ex.: 6232001234"
                                    />
                                </SimpleGrid>
                            </Stack>
                        </Box>

                        {/* ── Endereço ── */}
                        <Box style={card}>
                            <Stack gap={3}>
                                <HStack gap={2} style={cardHeader}>
                                    <Box style={{ width: '2px', height: '14px', background: '#f97316', borderRadius: '1px' }} />
                                    <Text fontSize="xs" fontWeight="bold" color="orange.400" textTransform="uppercase" letterSpacing="widest">
                                        Endereço
                                    </Text>
                                </HStack>

                                <Form.Location
                                    required
                                    defaultCity={userData?.address?.city}
                                    disabled={loading}
                                    onChange={value => setForm(prev => ({ ...prev, city_id: value }))}
                                />

                                <Form.Text
                                    required
                                    label="Logradouro"
                                    name="line_1"
                                    value={form.line_1 ?? ''}
                                    onChange={handleChange}
                                    error={errors?.line_1}
                                    disabled={loading}
                                    placeholder="Ex.: Av. Brasil Sul"
                                />

                                <SimpleGrid columns={2} gap={3}>
                                    <Form.Text
                                        required
                                        label="CEP"
                                        name="zip"
                                        value={form.zip ?? ''}
                                        onChange={handleChange}
                                        error={errors?.zip}
                                        disabled={loading}
                                        placeholder="Ex.: 74023-010"
                                    />
                                    <Form.Text
                                        required
                                        label="Bairro / Setor"
                                        name="neighborhood"
                                        value={form.neighborhood ?? ''}
                                        onChange={handleChange}
                                        error={errors?.neighborhood}
                                        disabled={loading}
                                        placeholder="Ex.: Setor Central"
                                    />
                                </SimpleGrid>

                                <SimpleGrid columns={2} gap={3}>
                                    <Form.Text
                                        label="Número"
                                        name="number"
                                        value={form.number ?? ''}
                                        onChange={handleChange}
                                        error={errors?.number}
                                        disabled={loading}
                                        placeholder="Ex.: 860"
                                    />
                                    <Form.Text
                                        label="Complemento"
                                        name="complement"
                                        value={form.complement ?? ''}
                                        onChange={handleChange}
                                        error={errors?.complement}
                                        disabled={loading}
                                        placeholder="Ex.: Apto 12"
                                    />
                                </SimpleGrid>

                                <Form.Text
                                    label="Complemento de endereço"
                                    name="line_2"
                                    value={form.line_2 ?? ''}
                                    onChange={handleChange}
                                    error={errors?.line_2}
                                    disabled={loading}
                                    placeholder="Ex.: Quadra 3"
                                />
                            </Stack>
                        </Box>

                    </SimpleGrid>

                    <SimpleGrid columns={onSkip ? 2 : 1} gap={3} mt={4}>
                        {onSkip && (
                            <Button
                                size="lg"
                                onClick={onSkip}
                                style={{
                                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    height: '48px',
                                    borderRadius: '10px',
                                    letterSpacing: '0.3px',
                                }}
                            >
                                Pular Etapa
                            </Button>
                        )}
                        <Button
                            type="submit"
                            size="lg"
                            loading={loading}
                            loadingText="Salvando..."
                            style={{
                                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '15px',
                                height: '48px',
                                borderRadius: '10px',
                                letterSpacing: '0.3px',
                            }}
                        >
                            Confirmar meus dados →
                        </Button>
                    </SimpleGrid>

                </Box>
            </Box>

        </Box>
    )
}
